import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import useVideoStore from '../store/videoStore';

const PageContainer = styled.div`
  width: 100%;
  margin: 0;
  background-color: transparent;
  overflow: hidden;
  position: relative;
`;

const ContentArea = styled.div`
  padding: 1.5rem 1rem 1rem;
  margin-top: 0;
`;

const VideoGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2.5rem;
`;

const VideoCard = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid var(--gray-100);
  border-top: 1px solid var(--gray-100);
  display: flex;
  flex-direction: column;
  min-height: 300px;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.08);
    border-color: var(--gray-200);
    border-top-color: var(--gray-200);
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  background-color: var(--gray-200);
  overflow: hidden;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 0 8px rgba(0,0,0,0.05);

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  ${VideoCard}:hover & img {
    transform: scale(1.05);
  }
`;

const PlayIconOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${VideoCard}:hover & {
    opacity: 1;
  }

  svg {
    width: 50px;
    height: 50px;
    color: white;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
  }
`;

const VideoInfo = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const VideoTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VideoDescription = styled.p`
  font-size: 0.9rem;
  color: #4B5563;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 1rem;
  flex: 1;
  min-height: 3rem;
`;

const ButtonContainer = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  padding-top: 0.75rem;
  border-top: 1px solid var(--gray-100);
  width: 100%;
`;

const CopyButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.3rem 0.6rem;
  background: linear-gradient(180deg, var(--gray-50, #F9FAFB) 0%, var(--gray-100, #F3F4F6) 100%);
  border: 1px solid var(--gray-200, #E5E7EB);
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--gray-600, #4B5563);
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  position: relative;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.01), inset 0 1px 0 rgba(255, 255, 255, 0.3);

  &:hover {
    background: linear-gradient(180deg, var(--gray-100, #F3F4F6) 0%, var(--gray-200, #E5E7EB) 100%);
    border-color: var(--gray-300, #D1D5DB);
    color: var(--secondary-color, #00529B);
    transform: translateY(-1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.08);
  }

  svg {
    width: 12px;
    height: 12px;
    stroke-width: 2;
    transition: transform 0.2s ease, color 0.2s ease;
    color: var(--gray-500, #6B7280);
  }

  &:hover svg {
     transform: scale(1.05);
     color: var(--secondary-color, #00529B);
  }
`;

const ButtonText = styled(motion.span)`
  display: inline-block;
  white-space: nowrap;
`;

const CopiedText = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--primary-color, #3B82F6);
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
    stroke-width: 2.2;
    color: var(--primary-color, #3B82F6);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;

  ${props => props.active ? `
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  ` : `
    background: white;
    color: var(--gray-600);
    border-color: var(--gray-200);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
      background: var(--gray-50);
      border-color: var(--gray-300);
      color: var(--gray-800);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}

  &:active {
    transform: translateY(0);
  }
`;

const FilterLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--gray-700);
  margin-right: 1rem;
`;

const VideoCount = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--gray-500);
  margin-left: 0.5rem;
  background: var(--gray-100);
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
`;

const CategoryBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: capitalize;
  z-index: 10;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;

  ${props => props.segment === 'neva' ? `
    background: rgba(255, 143, 0, 0.9);
    color: white;
    border: 1px solid rgba(255, 143, 0, 0.2);
  ` : `
    background: rgba(22, 40, 69, 0.9);
    color: white;
    border: 1px solid rgba(22, 40, 69, 0.2);
  `}

  ${VideoCard}:hover & {
    transform: scale(1.05);
    backdrop-filter: blur(6px);
    ${props => props.segment === 'neva' ? `
      background: rgba(255, 143, 0, 1);
      box-shadow: 0 2px 8px rgba(255, 143, 0, 0.3);
    ` : `
      background: rgba(22, 40, 69, 1);
      box-shadow: 0 2px 8px rgba(22, 40, 69, 0.3);
    `}
  }
`;

const YoutubePage = ({ onOpenVideoModal }) => {
  const { videos: allVideos, isLoading, fetchVideos } = useVideoStore();
  const [copiedVideoId, setCopiedVideoId] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    fetchVideos();
  }, []);

  const getFilteredVideos = () => {
    switch (selectedFilter) {
      case 'neva':
        return allVideos.filter(video => video.segment === 'neva');
      case 'unloading_station':
        return allVideos.filter(video => video.segment === 'unloading_station');
      case 'all':
      default:
        return allVideos;
    }
  };

  const filteredVideos = getFilteredVideos();
  const nevaVideos = allVideos.filter(video => video.segment === 'neva');
  const unloadingStationVideos = allVideos.filter(video => video.segment === 'unloading_station');

  const filterOptions = [
    { id: 'all', label: 'All videos', count: allVideos.length },
    { id: 'neva', label: 'Neva', count: nevaVideos.length },
    { id: 'unloading_station', label: 'Unloading Station', count: unloadingStationVideos.length }
  ];

  const handleVideoClick = (youtubeId) => {
    if (onOpenVideoModal) {
      onOpenVideoModal(youtubeId);
    }
  };

  const handleCopyLink = (videoUrl, youtubeId) => {
    navigator.clipboard.writeText(videoUrl).then(() => {
      setCopiedVideoId(youtubeId);

      setTimeout(() => {
        setCopiedVideoId(null);
      }, 2000);
    });
  };

  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ContentArea>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-500)' }}>
            Loading videos...
          </div>
        ) : (
        <>
        <FilterContainer>
          <FilterButtonGroup>
            <FilterLabel>Filter by category:</FilterLabel>
            {filterOptions.map((option) => (
              <FilterButton
                key={option.id}
                active={selectedFilter === option.id}
                onClick={() => setSelectedFilter(option.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                {option.label}
                <VideoCount>{option.count}</VideoCount>
              </FilterButton>
            ))}
          </FilterButtonGroup>
        </FilterContainer>

        <VideoGrid
          key={selectedFilter}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
          initial="hidden"
          animate="show"
        >
          {filteredVideos.map((video) => (
            <VideoCard
              key={video.id}
              onClick={() => handleVideoClick(video.youtube_id)}
              whileHover={{ scale: 1.03 }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0 }
              }}
              layout
            >
              <ThumbnailContainer>
                <img src={video.thumbnail_url} alt={video.title} />
                <CategoryBadge segment={video.segment}>
                  {video.segment === 'neva' ? 'Neva' : 'Unloading'}
                </CategoryBadge>
                <PlayIconOverlay>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </PlayIconOverlay>
              </ThumbnailContainer>
              <VideoInfo>
                <VideoTitle>{video.title}</VideoTitle>
                <VideoDescription>{video.description}</VideoDescription>
                <ButtonContainer>
                  <CopyButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyLink(video.youtube_url, video.youtube_id);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      {copiedVideoId === video.youtube_id ? (
                        <CopiedText
                          key="copied"
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          Copied!
                        </CopiedText>
                      ) : (
                        <ButtonText
                          key="copy"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Copy Link
                        </ButtonText>
                      )}
                    </AnimatePresence>
                  </CopyButton>
                </ButtonContainer>
              </VideoInfo>
            </VideoCard>
          ))}
        </VideoGrid>
        </>
        )}
      </ContentArea>
    </PageContainer>
  );
};

export default YoutubePage;
